import { Chip } from '@mui/material';
import { Fragment, useMemo } from 'react';
import * as yup from 'yup';
import { SchemaDescription } from 'yup';
import { DistributionSegment } from '../../../schemas/FundWaterfall.schema';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import {
  StyledTable,
  StyledTableCell,
  StyledTableHeaderRow,
  StyledTableRow,
} from '../../CompanyProfiles/components/Table/StyledGridComponents';
import { DistributionBar } from './DistributionBar2';
import {
  PER_CLASS_POOL_KEYS,
  useWaterfallResultsGrid,
  WaterfallResultsGridData,
  waterfallResultsGridDataSchema,
} from './useWaterfallResultsGrid';
import { WaterfallResultsSummaryRow } from './WaterfallResultsSummaryRow';

const OMIT_KEYS = ['tierOrder', 'remaining'] as const;

export function WaterfallResultsGrid() {
  const { tierData, summaryData, hasPerClassProceeds } = useWaterfallResultsGrid() ?? {};
  const fieldDescriptions = useMemo(() => {
    const omitKeys = [
      ...OMIT_KEYS,
      ...(!hasPerClassProceeds ? PER_CLASS_POOL_KEYS : (['visualization'] as const)),
    ];
    const schema = waterfallResultsGridDataSchema().omit(omitKeys);
    return Object.entries(schema.fields).map(([fieldKey, value]) => ({
      key: fieldKey as keyof WaterfallResultsGridData,
      desc: (value as yup.Schema).describe() as SchemaDescription,
    }));
  }, [hasPerClassProceeds]);

  const gridTemplateColumns = hasPerClassProceeds
    ? '3fr repeat(8, 2fr)'
    : '3fr 4fr 4fr 4fr repeat(4, 2fr)  7fr';

  const rows = useMemo(() => {
    if (!tierData) {
      return null;
    }
    return tierData.map((row: WaterfallResultsGridData) => (
      <StyledTableRow key={row.tierName}>
        {fieldDescriptions.map((f) => {
          const value = row[f.key as keyof WaterfallResultsGridData];
          let type: 'text' | 'numeric' = 'text';
          let formattedValue = String(value ?? '');
          if (f.desc.type === 'number') {
            type = 'numeric';
            formattedValue = FMT.format(f.desc.meta?.formatter || 'string', value);
          }
          if (f.key === 'visualization') {
            return value ? (
              <DistributionBar key={f.key} data={row as { visualization: DistributionSegment[] }} />
            ) : (
              <Fragment key={f.key}></Fragment>
            );
          }

          return (
            <StyledTableCell key={f.key} type={type!} style={getCellStyle(f.key, hasPerClassProceeds)}>
              {f.key === 'tierName' ? (
                <Chip sx={{ backgroundColor: colors.tertiary[20] }} label={formattedValue} />
              ) : (
                formattedValue
              )}
            </StyledTableCell>
          );
        })}
      </StyledTableRow>
    ));
  }, [tierData, fieldDescriptions, hasPerClassProceeds]);

  return (
    <>
      <StyledTable style={{ gridTemplateColumns }}>
        <StyledTableHeaderRow>
          {fieldDescriptions.map((f, i) => (
            <StyledTableCell
              key={f.key}
              style={i === 0 ? { paddingLeft: '1rem' } : getCellStyle(f.key, hasPerClassProceeds)}
              type={f.desc.type === 'number' ? 'numeric' : 'text'}
              header
            >
              {f.desc.label}
            </StyledTableCell>
          ))}
        </StyledTableHeaderRow>
        {rows}
        {summaryData && (
          <WaterfallResultsSummaryRow data={summaryData} hasPerClassProceeds={hasPerClassProceeds} />
        )}
      </StyledTable>
    </>
  );
}

function getCellStyle(key: string, hasPerClassProceeds?: boolean) {
  if (hasPerClassProceeds) {
    // With per-class pools: tierName(1) total(2) lpPool(3) lp(4) slpPool(5) slp(6) gpPool(7) gp(8) carry(9)
    // No spanning needed — all columns are uniform width
  } else {
    // Without: tierName(1) total(2) lp(3) slp(4) gp(5,6) carry(7,8) visualization(9)
    if (key === 'gp') {
      return { gridColumn: '5 / span 2' };
    }
    if (key === 'carry') {
      return { gridColumn: '7 / span 2' };
    }
  }
  return undefined;
}
